import { Dialog, DialogTitle, DialogContent, Box } from '@mui/material'
import { Instrument } from '../../../utils/types'
import { useMemo } from 'react'
import { usePostMqttResetNetwork, usePostSendOTAUpdateRequest } from '../../../mqtt/mqttHandlerContext'
import { useFirmwareVersions } from '../../../hooks/useFirmwareVersions'
import Text from '../../../assets/components/Text'
import BasicButton from '../../../assets/components/BasicButton'

import CloseIcon from '@mui/icons-material/Close'
import { Wifi, SystemUpdate } from '@mui/icons-material'
import { useCurrentUser } from '../../../hooks/contexts/currentUserContext'

interface SettingsDialogProps {
    openSettingsDialog: boolean
    polyglot: any
    instrument: Instrument
    onClose: (value: boolean) => void
    onStartOTA: (deviceId: string) => void
}

const SettingsDialog = ({ openSettingsDialog, polyglot, instrument, onClose, onStartOTA }: SettingsDialogProps) => {
    const resetCurrentNetwork = usePostMqttResetNetwork()
    const otaRequestUpdate = usePostSendOTAUpdateRequest()
    const currentUser = useCurrentUser()
    const isAdmin = currentUser?.admin ?? false

    const { data } = useFirmwareVersions()

    const isDeprecatedProtocol = instrument.protocol?.includes('1.0') ?? false

    const latestFirmware = useMemo(() => {
        if (!data || data.length === 0) return undefined
        return data[0]
    }, [data])

    const handleOTAUpdate = () => {
        if (latestFirmware?.url) {
            otaRequestUpdate(instrument.deviceId, latestFirmware.url)
        }
        onStartOTA(instrument.deviceId)
    }

    return (
        <Dialog
            open={openSettingsDialog}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return
                }
                onClose(false)
            }}
            fullWidth
        >
            {/* Close button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '0.5rem 0.5rem 0 0' }}>
                <CloseIcon onClick={() => onClose(false)} sx={{ cursor: 'pointer' }} />
            </Box>

            {/* Title */}
            <DialogTitle sx={{ textAlign: 'center', pt: 0 }} fontWeight="bold" fontSize="1.5rem">
                {polyglot.t('settingDialog.heading')}
            </DialogTitle>

            {/* Content */}
            <DialogContent
                sx={{
                    m: '0 0.5rem 0.5rem 0.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '1rem'
                }}
            >
                {isDeprecatedProtocol ? <ResetNetworkSection instrument={instrument} polyglot={polyglot} onReset={resetCurrentNetwork} /> : <FirmwareSection isAdmin={isAdmin} polyglot={polyglot} instrument={instrument} latestFirmware={latestFirmware} handleOTAUpdate={handleOTAUpdate} />}
            </DialogContent>
        </Dialog>
    )
}

export default SettingsDialog

// ==========================
// 🔽 Internal components
// ==========================

interface ResetProps {
    instrument: Instrument
    polyglot: any
    onReset: (deviceId: string) => void
}

const ResetNetworkSection = ({ instrument, polyglot, onReset }: ResetProps) => {
    return (
        <>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <Wifi />
                <Text size="heading">{polyglot.t('settingDialog.resetNetwork')}</Text>
            </Box>

            <Text size="standard" italic>
                {polyglot.t('settingDialog.resetNetworkDescription')}
            </Text>

            <BasicButton title={polyglot.t('settings.license.resetWifiPasswordBtn')} variant="contained" size="small" onClick={() => onReset(instrument.deviceId)} />
        </>
    )
}

// --------------------------

interface FirmwareProps {
    instrument: Instrument
    latestFirmware?: {
        version: string
        url: string
    }
    handleOTAUpdate: () => void
    polyglot: any
    isAdmin: boolean
}

const FirmwareSection = ({ instrument, latestFirmware, handleOTAUpdate, polyglot, isAdmin }: FirmwareProps) => {
    const hasLatestFirmware = () => {
        if (instrument?.firmware && latestFirmware?.version) {
            return instrument.firmware >= latestFirmware.version
        }
        return true
    }

    return (
        <>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <SystemUpdate />
                <Text size="heading">{polyglot.t('settingDialog.firmwareVersion')}</Text>
            </Box>

            {!instrument.firmware ? (
                <Text size="standard" italic>
                    {polyglot.t('settingDialog.noFirmwareInfo')}
                </Text>
            ) : hasLatestFirmware() ? (
                <>
                    <Text size="standard" italic>
                        {`Version: ${instrument.firmware}`}
                    </Text>
                    <Text size="standard" italic>
                        {polyglot.t('settingDialog.firmwareUpToDate')}
                    </Text>
                </>
            ) : (
                <>
                    <Text size="standard" italic>
                        {`Version: ${instrument.firmware}`}
                    </Text>

                    {isAdmin && (
                        <>
                            <Text size="standard" italic>
                                {polyglot.t('settingDialog.firmwareAvailable')}
                            </Text>
                            <BasicButton
                                title={polyglot.t('settingDialog.downloadUpdateBtn')}
                                variant="contained"
                                onClick={() => {
                                    handleOTAUpdate()
                                }}
                            />
                        </>
                    )}
                </>
            )}
        </>
    )
}
